import { FC } from "react";
import { NumberInput, Stack, Text } from "@mantine/core";
import { normalizeDollarBound } from "../consts";

/**
 * EIS-1093: a REUSABLE min/max DOLLAR range editor.
 *
 * Same editor shape as LastCheckInFilter (between / input / to / input, the
 * bottom Set to Default + Apply buttons come from FilterModal), with `$` instead
 * of `Days`.
 *
 * EIS-1097 turns it from a factory parameterised by a triple of
 * `keyof FilterControl` field names into a PLAIN CONTROLLED COMPONENT. Those
 * fields no longer exist: the range is now min/max INSIDE the previousSales
 * discriminated union, reachable only when the Has segment is selected, so a
 * pair of top-level field names can no longer address it. Value-in /
 * onChange-out is also what the sibling "Prior YTD Sales" ticket needs -- it can
 * nest this anywhere without the editor knowing anything about the shape of the
 * filter state.
 *
 * `fontSize: 24` on the inputs is LOAD-BEARING: iOS zooms the whole page when a
 * focused input's font is under 16px. Do not shrink it.
 *
 * NOTE: EIS-1093's warning that a $0 minimum also swept in accounts that never
 * bought anything is deliberately NOT here. That trap INVERTED when the range
 * moved inside the Has segment: the range is now only ever ANDed with
 * `sales_in_window > 0`, so a $0 minimum can no longer admit a never-bought
 * account, and repeating the warning would be telling the user something false.
 * See PreviousSalesState in ../consts. Do NOT re-add a $1 clamp either.
 *
 * EIS-1097 round 3 DOES warn about a range that cannot match anything (a maximum
 * of $0 or less, or a minimum above the maximum), but that warning lives in
 * PreviousSalesFilter, not here: "a maximum of $0 rules everything out" is a
 * fact about the Has segment's own `> 0`, not about dollar ranges in general,
 * and this editor is meant to stay reusable by the sibling "Prior YTD Sales"
 * row. It is a warning, never a correction -- the bounds below still go to the
 * server exactly as typed.
 */

const MONTH_ABBREVIATIONS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * The caption naming the window the money is measured over, e.g.
 * "Aug 2025 – Aug 2026 · last 12 months".
 *
 * COMPUTED FROM THE CURRENT DATE on purpose -- it is a ROLLING window, so a
 * hardcoded "Aug 2025" would be silently wrong from September onwards and would
 * mislabel the very number the user is filtering on. `now` is injectable so the
 * unit test can pin the format without depending on the day it runs.
 *
 * EIS-1097 parameterises the LENGTH too: the window is user-selectable
 * (3/6/12/24), and a caption still reading "last 12 months" under a 6-month
 * selection would be the same lie in a different place.
 */
export const salesWindowCaption = (
  months: number,
  now: Date = new Date(),
): string => {
  // The month arithmetic is done DIRECTLY, never through
  // `new Date(y, m - months, now.getDate())`: when the current day-of-month does
  // not exist in the target month ("Feb 31"), Date NORMALISES the overflow
  // FORWARD into the next month and the caption then names the wrong start month
  // -- May 31 with a 3-month window read "Mar 2026" for a window that begins in
  // February. The server computes the real window with
  // `relativedelta(months=N)`, which CLAMPS instead (May 31 - 3 months = Feb 28),
  // so the start MONTH is simply now's month minus `months`. The caption only
  // ever renders month + year, so no day is needed at all.
  const startMonthOffset = now.getMonth() - months;
  const startYear = now.getFullYear() + Math.floor(startMonthOffset / 12);
  const startMonth = ((startMonthOffset % 12) + 12) % 12;
  const format = (year: number, month: number) =>
    `${MONTH_ABBREVIATIONS[month]} ${year}`;
  return `${format(startYear, startMonth)} – ${format(now.getFullYear(), now.getMonth())} · last ${months} months`;
};

/** NumberInput wants "" for "empty", never null/undefined. */
const toInputValue = (value: unknown): number | "" =>
  (typeof value === "number" ? value : "");

const numberInputStyles = {
  input: {
    fontSize: 24,        // ← stops iOS zoom on focus
    paddingTop: 0,
    paddingBottom: 0,
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    textAlign: "center" as const,
  },
};

export interface DollarRangeInputsProps {
  min: number | null;
  max: number | null;
  onChange: (bounds: { min: number | null; max: number | null }) => void;
  /** data-testid prefix so each instance is independently addressable. */
  testIdPrefix: string;
}

export const DollarRangeInputs: FC<DollarRangeInputsProps> = ({
  min,
  max,
  onChange,
  testIdPrefix,
}) => (
  <Stack align="center" gap="xs" data-testid={`${testIdPrefix}-range`}>
    <Text>
      between
    </Text>
    <NumberInput
      w="140px"
      hideControls
      value={toInputValue(min)}
      onChange={(e) => onChange({ min: normalizeDollarBound(e), max })}
      prefix="$"
      thousandSeparator=","
      decimalSeparator="."
      inputMode="decimal"
      data-testid={`${testIdPrefix}-min-input`}
      styles={numberInputStyles} />
    <Text>
      to
    </Text>
    <NumberInput
      w="140px"
      hideControls
      value={toInputValue(max)}
      onChange={(e) => onChange({ min, max: normalizeDollarBound(e) })}
      prefix="$"
      placeholder="no limit"
      thousandSeparator=","
      decimalSeparator="."
      inputMode="decimal"
      data-testid={`${testIdPrefix}-max-input`}
      styles={numberInputStyles} />
    {/* A BLANK maximum is "and up", not $0 -- say so, so the user never has to
        guess whether an empty box means "unbounded" or "zero". */}
    {max === null ? (
      <Text
        fz="sm"
        c="#4F7D9C"
        ta="center"
        data-testid={`${testIdPrefix}-no-upper-bound-caption`}>
        {min !== null
          ? `$${min.toLocaleString("en-US")} and up`
          : "no upper limit"}
      </Text>
    ) : null}
  </Stack>
);

export { normalizeDollarBound };

export default DollarRangeInputs;
